// @ts-strict-ignore
import { getIsOTAPlatform } from '@/helpers';
import { relativeTimingLabel, tooltipDisplayLifecycleEventName } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectAppStoreType,
    selectAutomationsByType,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsWorkflowsMigratedAndEnabled,
    selectStepConfigDataFromStepGraphs,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import { AppStoreType, AutomationType, LifecycleEventType, RelativeTiming, ReleaseStepType } from '@/types';
import { Nullable } from '@/typescriptHelpers';
import React from 'react';
import { useSelector } from 'react-redux';

enum DestinationSelector {
    ANY = 'Any',
    ALL = 'All',
}

export const ROLLOUT_THRESHOLD_LIFECYCLE_EVENT_TYPES = [
    LifecycleEventType.LifecycleEventTypeRolloutPercentage,
    LifecycleEventType.LifecycleEventTypeRolloutDay,
];

// Keep a stable empty fallback for org-level usage so selector results don't
// change identity on every render and retrigger form initialization effects.
const EMPTY_OBJECT = {};

const createOptionForEvent = (event: LifecycleEventType, destinationSelector?: DestinationSelector) => {
    if (!destinationSelector) {
        return { value: event, label: tooltipDisplayLifecycleEventName(event) };
    }

    return {
        value: `${event}:${destinationSelector}`,
        label: `${tooltipDisplayLifecycleEventName(event)} (${destinationSelector})`,
    };
};

export const useRelativeTimeTriggerOptions = (appId: Nullable<string>, stepConfigId: Nullable<string>) => {
    const isWorkflowsEnabled = useSelector((state: RootState) =>
        appId ? selectIsWorkflowsMigratedAndEnabled(state, appId) : false,
    );
    // Select booleans, not the steps object: a new object identity rebuilds the options and resets the open form
    const isSubmissionStepEnabled = useSelector((state: RootState) =>
        appId ? !!selectWorkflowEnabledSteps(state, appId)[ReleaseStepType.Submission] : false,
    );
    const isRolloutStepEnabled = useSelector((state: RootState) =>
        appId ? !!selectWorkflowEnabledSteps(state, appId)[ReleaseStepType.Rollout] : false,
    );
    const stepGraphStepsMap = useSelector((state: RootState) =>
        appId ? selectStepConfigDataFromStepGraphs(state, appId) : null,
    );
    const destinationStepConfigIds =
        stepGraphStepsMap ? (stepGraphStepsMap[stepConfigId]?.destinationStepConfigIds ?? []) : [];
    const destinationsByStepConfigId = useSelector((state: RootState) =>
        appId ? selectDestinationInfoByDestinationStepConfigId(state, appId) : EMPTY_OBJECT,
    );

    const destinations = React.useMemo(() => {
        return destinationStepConfigIds.map((d) => destinationsByStepConfigId[d]).filter((d) => !!d);
    }, [destinationStepConfigIds, destinationsByStepConfigId]);

    const appStoreType = useSelector((state: RootState) => (appId ? selectAppStoreType(state, appId) : undefined));
    const isAutoStagedRolloutEnabled = useSelector((state: RootState) =>
        appId ?
            (selectAutomationsByType(state, appId, AutomationType.AutoStagedRollout) ?? []).some(
                (automation) =>
                    automation.enabled ||
                    Object.values(automation.configPerReleaseType ?? {}).some((override) => override?.enabled),
            )
        :   false,
    );
    // Other stores never record which rollout day a release is on, and Google only does while auto staged rollout runs, so a day trigger would only fire at 100%
    const hasRolloutDays = React.useMemo(() => {
        const appStoreTypes = destinations.length > 0 ? destinations.map((d) => d.appStoreType) : [appStoreType];
        return appStoreTypes.some(
            (type) =>
                !type || type === AppStoreType.Apple || (type === AppStoreType.Google && isAutoStagedRolloutEnabled),
        );
    }, [destinations, appStoreType, isAutoStagedRolloutEnabled]);

    const isWorkflowsEnabledAndMultiDestination = React.useMemo(() => {
        return isWorkflowsEnabled && destinations.length > 1;
    }, [isWorkflowsEnabled, destinations]);

    const relativeTimingOptions = Object.values(RelativeTiming).map((value: RelativeTiming) => ({
        value,
        label: relativeTimingLabel(value),
    }));

    // OTA-only destinations have no submission/rollout lifecycle events, so hide those.
    const hasOnlyOTADestinations = React.useMemo(
        () => destinations.length > 0 && destinations.every((d) => getIsOTAPlatform(d.platform)),
        [destinations],
    );

    const lifecycleEventOptions = React.useMemo(() => {
        const options = [createOptionForEvent(LifecycleEventType.LifecycleEventTypeKickoff)];

        if (!hasOnlyOTADestinations) {
            if (isWorkflowsEnabledAndMultiDestination && isSubmissionStepEnabled) {
                options.push(
                    createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit, DestinationSelector.ANY),
                );
                options.push(
                    createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit, DestinationSelector.ALL),
                );
            } else {
                options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeSubmit));
            }
        }

        if (isWorkflowsEnabledAndMultiDestination && isSubmissionStepEnabled) {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease, DestinationSelector.ANY));
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease, DestinationSelector.ALL));
        } else {
            options.push(createOptionForEvent(LifecycleEventType.LifecycleEventTypeRelease));
        }

        if (!hasOnlyOTADestinations) {
            for (const rolloutEvent of [
                LifecycleEventType.LifecycleEventTypeRolloutCompletion,
                LifecycleEventType.LifecycleEventTypeRolloutPercentage,
                ...(hasRolloutDays ? [LifecycleEventType.LifecycleEventTypeRolloutDay] : []),
            ]) {
                if (isWorkflowsEnabledAndMultiDestination && isRolloutStepEnabled) {
                    options.push(createOptionForEvent(rolloutEvent, DestinationSelector.ANY));
                    options.push(createOptionForEvent(rolloutEvent, DestinationSelector.ALL));
                } else {
                    options.push(createOptionForEvent(rolloutEvent));
                }
            }
        }

        return options;
    }, [
        hasOnlyOTADestinations,
        hasRolloutDays,
        isWorkflowsEnabledAndMultiDestination,
        isSubmissionStepEnabled,
        isRolloutStepEnabled,
    ]);

    const parseSelectedLifecycleEventOption = React.useCallback((value: string) => {
        const [lifecycleEventType, waitForAllDestinations] = value.split(':');
        return {
            lifecycleEventType: lifecycleEventType as LifecycleEventType,
            waitForAllDestinations: !waitForAllDestinations || waitForAllDestinations === DestinationSelector.ALL,
        };
    }, []);

    const convertToSelectedLifecycleEventValue = React.useCallback(
        (eventType: LifecycleEventType, waitForAllDestinations: boolean) => {
            const selector = waitForAllDestinations ? DestinationSelector.ALL : DestinationSelector.ANY;
            const { value } = createOptionForEvent(eventType, selector);
            return lifecycleEventOptions.find((option) => option.value === value) ? value : eventType;
        },
        [lifecycleEventOptions],
    );

    return {
        relativeTimingOptions,
        lifecycleEventOptions,
        parseSelectedLifecycleEventOption,
        convertToSelectedLifecycleEventValue,
    };
};
